import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useConvexAuth } from "convex/react";
import { api } from "convex/_generated/api";
import { useEffect, useState } from "react";
import { useTranslation } from "@/packages/utils";
import { BrandLockup } from "@/components/branding/BrandLockup";
import { Button } from "@/components/ui/primitives";
import { Loader2, AlertCircle, Mail } from "lucide-react";

export const Route = createFileRoute("/invite/$token")({
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useConvexAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({
        to: "/auth",
        search: {
          flow: "signUp",
          returnTo: `/invite/${token}`,
        },
      });
    }
  }, [isAuthenticated, isLoading, token, navigate]);

  if (isLoading) {
    return (
      <InviteLayout>
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted" />
        </div>
      </InviteLayout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <AcceptInviteCard token={token} />;
}

function AcceptInviteCard({ token }: { token: string }) {
  const { t } = useTranslation(["auth"]);
  const navigate = useNavigate();
  const acceptInvite = useMutation(api.workspace_invites.accept);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setIsAccepting(true);
    setError(null);
    try {
      const result = await acceptInvite({ token });
      navigate({
        to: "/$workspaceSlug/dashboard",
        params: { workspaceSlug: result.slug },
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to accept invite";
      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes("expired")) {
        setError(t("auth:invite.errors.expired"));
      } else if (
        lowerMessage.includes("not found") ||
        lowerMessage.includes("invalid")
      ) {
        setError(t("auth:invite.errors.invalid"));
      } else if (
        lowerMessage.includes("different email") ||
        lowerMessage.includes("email address")
      ) {
        setError(t("auth:invite.errors.emailMismatch"));
      } else {
        setError(t("auth:invite.errors.unknown"));
      }
      setIsAccepting(false);
    }
  }

  if (error) {
    return (
      <InviteLayout>
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-primary">
              {t("auth:invite.inviteError")}
            </h1>
            <p className="text-sm text-secondary">{error}</p>
          </div>
          <Link
            to="/"
            className="text-sm font-medium text-accent hover:text-accent/80 transition-colors"
          >
            Go to app
          </Link>
        </div>
      </InviteLayout>
    );
  }

  return (
    <InviteLayout>
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
          <Mail className="h-6 w-6 text-accent" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-primary">
            {t("auth:invite.youveBeenInvited")}
          </h1>
          <p className="text-sm text-secondary">
            {t("auth:invite.joinWorkspaceDescription")}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleAccept}
          loading={isAccepting}
          className="w-full"
        >
          {isAccepting
            ? t("auth:invite.joining")
            : t("auth:invite.acceptAndJoinWorkspace")}
        </Button>
      </div>
    </InviteLayout>
  );
}

function InviteLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-page p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-8">
        <div className="flex items-center justify-center">
          <BrandLockup
            iconClassName="size-8"
            textClassName="text-[18px] font-semibold"
          />
        </div>
        <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}
